import React, { useEffect, useState } from 'react';
import { Problem } from '../../../../core/problems/types';
import { Button, Grid, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import DatasetListItem from './DatasetListItem';
import CreateDatasetModal from './CreateDatasetModal';
import { Dataset } from '../../../../core/datasets/types';
import { getContestDatasets, updateDatasetOrder } from '../../../../core/datasets/services';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import { LinearLoader } from '../../../../design-library/LinearLoader';
import {
	DragDropContext,
	Draggable,
	DraggableProvided,
	Droppable,
	DroppableProvided,
	DropResult,
} from '@hello-pangea/dnd';

export default function ProblemDataset({ problem }: { problem: Problem }) {
	const [isDatasetCreateModalOpen, setIsDatasetCreateModalOpen] = useState<boolean>(false);
	const [datasets, setDatasets] = useState<Dataset[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isOrderChanged, setIsOrderChanged] = useState<boolean>(false);

	useEffect(() => {
		setIsLoading(true);
		getContestDatasets(problem.id!, problem.problem_set!.id)
			.then((datasets: Dataset[]) => {
				setDatasets(datasets);
			})
			.catch((e) => {
				if (axios.isAxiosError(e)) {
					enqueueSnackbar(`Something went wrong fetching the datasets!`, { variant: 'error' });
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, []);

	const handleDatasetDeleteSuccess = (datasetId: number) => {
		setDatasets(datasets.filter((dataset) => dataset.id !== datasetId));
	};

	const handleDatasetCreateSuccess = (dataset: Dataset) => {
		setDatasets([...datasets, dataset]);
	};

	const onDragEnd = (result: DropResult) => {
		if (!result.destination) return;

		const updatedDatasets = Array.from(datasets);
		const [movedItem] = updatedDatasets.splice(result.source.index, 1);
		updatedDatasets.splice(result.destination.index, 0, movedItem);

		// Re calculate order for all dataset.
		// Changing order of one dataset will affect others. So we need calculate the new order for all of them.
		updatedDatasets.forEach((dataset, index) => (dataset.test_case_order = index + 1));

		setDatasets(updatedDatasets);
		setIsOrderChanged(true);
	};

	const handleSaveOrder = () => {
		updateDatasetOrder(problem.id!, datasets)
			.then(() => {
				enqueueSnackbar('Dataset order updated successfully!', { variant: 'success' });
				setIsOrderChanged(false);
			})
			.catch(() => {
				enqueueSnackbar('Failed to update dataset order.', { variant: 'error' });
			});
	};

	if (isLoading) return <LinearLoader />;

	return (
		<Grid container direction='column' justifyContent='center' sx={{ width: '100%', gap: 2 }}>
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable droppableId='dataset-list' direction='vertical'>
					{(provided: DroppableProvided) => (
						<Grid item container direction='row' spacing={2} {...provided.droppableProps} ref={provided.innerRef}>
							{datasets.map((dataset, index) => (
								<Draggable key={dataset.id!} draggableId={String(dataset.id!)} index={index}>
									{(provided: DraggableProvided) => (
										<Grid
											item
											xs={12}
											ref={provided.innerRef}
											{...provided.draggableProps}
											{...provided.dragHandleProps}
											sx={{
												position: 'relative',
												'&:hover .drag-handle': {
													opacity: 1,
												},
											}}
										>
											<Box
												className='drag-handle'
												sx={{
													position: 'absolute',
													left: -10,
													top: '50%',
													transform: 'translateY(-50%)',
													opacity: 0,
													transition: 'opacity 0.2s ease-in-out',
													zIndex: 1,
													cursor: 'grab',
													color: 'primary.main',
													'&:active': {
														cursor: 'grabbing',
													},
												}}
											>
												<DragHandleIcon />
											</Box>
											<DatasetListItem
												dataset={dataset}
												onDeleteSuccess={handleDatasetDeleteSuccess}
												problemSetId={problem.problem_set!.id}
											/>
										</Grid>
									)}
								</Draggable>
							))}
							{provided.placeholder}
						</Grid>
					)}
				</Droppable>
			</DragDropContext>

			<Grid item container direction='row' spacing={2}>
				<Grid item>
					<Button startIcon={<AddIcon />} variant='contained' onClick={() => setIsDatasetCreateModalOpen(true)}>
						Add New Dataset
					</Button>
				</Grid>
				{isOrderChanged && (
					<Grid item>
						<Button variant='contained' color='primary' onClick={handleSaveOrder}>
							Save
						</Button>
					</Grid>
				)}
			</Grid>

			<CreateDatasetModal
				isOpen={isDatasetCreateModalOpen}
				onClose={() => setIsDatasetCreateModalOpen(false)}
				problemId={problem.id!}
				onSuccess={handleDatasetCreateSuccess}
				problemSetId={problem.problem_set!.id}
			/>
		</Grid>
	);
}
